import { PageTypes, HostNameRegExps } from '../constants';
import { defaultLocales } from '../constants/defaultLocales';
import {
  PRODUCT_CODE_REGEXP,
  LEAN_PRODUCT_CODE_REGEXP,
  COMBINABLE_LOCALE_REGEX,
  LEAN_LOCALE_REGEX,
} from '../constants/regex';
import type { getClient } from '~/utilities/graphql/apolloClient';
import type { SiteType } from '~/utilities/graphql/codegen';
import { fetchCategoryFacetsDetails } from '~/utilities/graphql/queries';

const PDP_PATH_REGEX = new RegExp(
  String.raw`^${COMBINABLE_LOCALE_REGEX.source}/shop/(.*/)?${LEAN_PRODUCT_CODE_REGEXP.source}(\?.*)?$`,
  'i'
);

const PLP_PATH_REGEX = new RegExp(
  String.raw`^${COMBINABLE_LOCALE_REGEX.source}/shop(/[a-zA-Z0-9][a-zA-Z0-9_-]*)*(\?.+)?$`,
  'i'
);

const SRP_PATH_REGEX = new RegExp(
  String.raw`^${COMBINABLE_LOCALE_REGEX.source}/search(\?.*)?$`,
  'i'
);

export function isPDP(path: string) {
  return PDP_PATH_REGEX.test(path);
}

export function isPLP(path: string) {
  return PLP_PATH_REGEX.test(path) && !isPDP(path);
}

export function isSearch(path: string) {
  return SRP_PATH_REGEX.test(path);
}

export function isValidProductCode(productCode: string): boolean {
  return PRODUCT_CODE_REGEXP.test(productCode);
}

export function getProductCode(url: string): string | null {
  const productCode = url.split('/').at(-1)?.split('?').at(0);

  if (productCode && isValidProductCode(productCode)) {
    return productCode;
  }

  return null;
}

export function getCategoryPathsFromPDPUrl(pdpUrl?: string | null) {
  const paths = pdpUrl?.split('/') || [];

  if (pdpUrl?.length && LEAN_LOCALE_REGEX.test(pdpUrl)) {
    paths.splice(1, 1);
  }

  const superCategory = paths[2];
  const pageCategory = paths[3];
  const plpPath = paths.slice(0, -1).join('/');

  return { superCategory, pageCategory, plpPath };
}

export function isValidOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);

    return Object.values(HostNameRegExps).some(regex => regex.test(url.host));
  } catch {
    return false;
  }
}

export function isValidLocale(locale: string): boolean {
  const locales = `${process.env.LOCALES}`.split(',');
  const defaultIndex = Number(process.env.DEFAULT_LOCALE_INDEX);

  if (Array.isArray(locales)) {
    const localeIndex = locales.indexOf(locale.toLocaleLowerCase());

    return localeIndex >= 0 && localeIndex !== defaultIndex;
  }

  return false;
}

export function isMatchLocaleCountry(ipLocale: string, locale: string): boolean {
  const ipLocaleCountry = ipLocale.split('_')[1];
  const localeCountry = locale.split('_')[1];

  return ipLocaleCountry === localeCountry;
}

export function getCountryCodeFromAkamaiHeader(akamaiEdgescape: string | null): string | null {
  try {
    const obj = JSON.parse(`{"${akamaiEdgescape}"}`.replace(/=/g, '":"').replace(/,/g, '","'));

    return obj.country_code?.toLowerCase();
  } catch (e) {
    return null;
  }
}

export function getDefaultLocaleFromCountryCode(countryCode: string | null | undefined): string {
  return defaultLocales[countryCode?.toLowerCase() || ''] || 'default';
}

/**
 * Not all Hybris given Invalidation Page Types are applicable for storefront.
 *
 * Possible values from Hybris cache invalidation are:
 * PLP
 * PDP
 * PDP_DATA
 * HOME
 * CONTENT
 * SYSTEM_TEXTS
 * DYNAMIC_FORM
 * VALIDATION_RULES
 * CONFIGURATION
 * COUNTRIES
 * PRODUCT_EXPERIENCE
 * PRODUCT
 * PAYMENT_METHODS
 * SOCIAL_MEDIA_CHANNELS
 * SEARCH_SUGGESTIONS
 * CHECKOUT
 * ORDER_CONFIRMATION_PAGE
 * LOYALTY
 * PRODUCT_USP
 */
export function isInvalidationPageTypeApplicable(invalidationPageType: string): boolean {
  return [
    '.*',
    'ALL',
    'PDP',
    'PLP',
    'PRODUCT',
    'SOCIAL_MEDIA_CHANNELS',
    'PAYMENT_METHODS',
    'CONFIGURATION',
    'CONTENT',
    'HOME',
  ].includes(invalidationPageType);
}

const ARBITRARY_NUMBER_SUM = 1337;
const ARBITRARY_NUMBER_DIV = 4;
const UNIX_TIMESTAMP = 1_000_000_000;

export const isValidGracCookie = (cookie: string) => {
  const value = Number(cookie || undefined);

  if (Number.isNaN(value)) {
    return false;
  }

  const parsedValue = (value + ARBITRARY_NUMBER_SUM) / ARBITRARY_NUMBER_DIV + UNIX_TIMESTAMP;
  const currentTime = Date.now() / 1000;
  const timeDifference = parsedValue - currentTime;

  return timeDifference > 0 && timeDifference <= 14400;
};

export const ONE_MINUTE = 60 * 1;

const TWO_MINUTES = 60 * 2;
const EIGHT_MINUTES = 60 * 8;
const HALF_HOUR = 60 * 30;
const ONE_DAY = 60 * 60 * 24;

/**
 * Manually extracted from NextJS's @GetStaticPropsResult
 */
type NotFoundProps = {
  notFound: true;
  revalidate?: number | boolean;
};

export const NOT_FOUND_REVALIDATE: NotFoundProps = {
  notFound: true,
  revalidate: EIGHT_MINUTES,
};

/**
 * Revalidate time per page type
 *
 * @see https://g-star.atlassian.net/browse/FS-3894
 */
export const PAGES_REVALIDATE_TIME: Record<PageTypes, number> = {
  [PageTypes.HOME]: TWO_MINUTES,
  [PageTypes.CATEGORY]: EIGHT_MINUTES,
  [PageTypes.PRODUCT]: HALF_HOUR,
  [PageTypes.SEARCH]: ONE_DAY,
  [PageTypes.CAMPAIGN]: ONE_DAY,
  [PageTypes.STORIES]: ONE_DAY,
  [PageTypes.LOOKBOOK]: ONE_DAY,
  [PageTypes.WISHLIST]: ONE_DAY,
  [PageTypes.OTHER]: ONE_DAY,
  [PageTypes.PDV]: 0,
  [PageTypes.CUSTOMER_SERVICE]: ONE_DAY,
  [PageTypes.CUSTOMER_SERVICE_RETURNS]: ONE_DAY,
} as const;

/**
 * homepage is skipped because it is pre-generated by default as it's the entrypoint into the app
 */
type SSR_PAGE_TYPES =
  | 'PLP'
  | 'PDP'
  | 'SRP'
  | 'STORIES'
  | 'LOOKBOOK'
  | 'CAMPAIGN'
  | 'WISHLIST'
  | 'CUSTOMER_SERVICE'
  | 'CUSTOMER_SERVICE_RETURNS';

export const shouldPrebuildPage = (page: SSR_PAGE_TYPES) =>
  process.env.PAGE_TYPES_TO_BUILD?.includes(page);

export const fetchPDPJeansCategoryDetails = async ({
  categoryPath,
  siteType,
  locale,
  apolloClient,
}: {
  categoryPath?: string;
  siteType: SiteType;
  locale: string;
  apolloClient: ReturnType<typeof getClient>;
}) => {
  if (!categoryPath?.includes('jeans')) {
    return null;
  }

  const categoryResponse = await fetchCategoryFacetsDetails({
    siteType,
    locale,
    path: categoryPath,
    refetch: true,
    apolloClient,
  });

  return categoryResponse.categoryDetails;
};

/**
 * Retryable HTTP Client Errors
 *
 * - 408 Request Timeout
 * - 429 Too Many Requests
 */
export const RETRYABLE_4XX_ERRORS = new Set([408, 429]);

const ERRORS_5XX = [502, 503, 504];

/**
 * Retryable HTTP Server Errors
 *
 * - 502 Bad Gateway
 * - 503 Service Unavailable
 * - 504 Gateway Timeout
 */
export const RETRYABLE_5XX_ERRORS = new Set(ERRORS_5XX);

/**
 * Returns true for transient backend errors (502/503/504) that may resolve on retry.
 * Used to distinguish between permanent 404s and temporary unavailability during SSG/ISR.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof Error && 'networkError' in error) {
    const ne = (error as { networkError?: { statusCode?: number } }).networkError;

    if (ne?.statusCode) {
      return RETRYABLE_5XX_ERRORS.has(ne.statusCode);
    }
  }

  // Fallback: match ApolloError message format "Response not successful: Received status code 50x"
  if (error instanceof Error) {
    const values = ERRORS_5XX.join('|');
    const regex = new RegExp(`status code (${values})`, 'im');

    return regex.test(error.message);
  }

  return false;
}
